/**
 * File: useADRDefinitionListener.ts
 * Description: Custom hook to handle ADR definition creation events
 * Author: GitHub Copilot
 * Created: 2025-01-XX
 * Last Modified: 2025-01-XX
 */

import { useEffect } from 'react';

import { ADRDefinition } from '../../types/adr';

interface UseADRDefinitionListenerProps {
  projectId: string | undefined;
  onDefinitionCreated: (definition: ADRDefinition) => void;
}

/**
 * Hook to listen for ADR definition creation events
 */
export const useADRDefinitionListener = ({
  projectId,
  onDefinitionCreated,
}: UseADRDefinitionListenerProps): void => {
  useEffect(() => {
    const handleDefinitionCreated = (event: Event) => {
      const customEvent = event as CustomEvent<{
        definition?: ADRDefinition;
        projectId?: number | string;
      }>;
      const { definition, projectId: eventProjectId } = customEvent.detail ?? {};

      if (!definition) {
        return;
      }

      if (projectId && eventProjectId && Number.parseInt(projectId, 10) !== Number(eventProjectId)) {
        return;
      }

      onDefinitionCreated(definition);
    };

    globalThis.addEventListener('adrDefinitionCreated', handleDefinitionCreated as EventListener);

    return () => {
      globalThis.removeEventListener('adrDefinitionCreated', handleDefinitionCreated as EventListener);
    };
  }, [projectId, onDefinitionCreated]);
};
